import { useEffect, useRef, useState } from 'react'
import { useCreatePayment, useLoadSdkYuno } from '../../../../hooks'
import { queryClient } from '../../../../state'
import { CheckoutInfo, CreatePaymentParams } from '../../../../types'
import {
  getCustomButtonStyles,
  getPaymentMethodTypeList,
} from '../payment-methods.utils'
import { useTranslation } from 'react-i18next'
import { dispatchLoaderEvent } from '../../../../utils/loader'
import { shouldHoldLoader } from '../../../../utils/should-hold-loader'
import {
  setCheckoutReturnUrlInLS,
  setPaymentIdInLS,
  setPublicApiKeyInLS,
  setShowResultScreenInLS,
} from '../../../../utils/local-storage'
import {
  buildCallbackUrl,
  getVtexResultScreenOverride,
  redirectAfterResultScreen,
  redirectTo,
} from '../../../../utils'
import { shouldSkipContinuePayment } from './should-skip-continue-payment'
import { getWhiteLabelOptions } from '../../../../utils/white-label'

const LANGUAGE_CHANGE_DEBOUNCE_MS = 400

export const usePaymentMethodsMobile = () => {
  const { isYunoLoaded } = useLoadSdkYuno()
  const [yunoInstance, setYunoInstance] = useState<YunoInstance | undefined>()
  const [onlyExpressPaymentMethods, setOnlyExpressPaymentMethods] = useState(false)
  const getPaymentData = useRef<(oneTimeToken: string) => CreatePaymentParams>()
  const paymentIdRef = useRef<string | null>(null)
  const isInitialMount = useRef(true)
  const checkoutInfo = queryClient.getQueryData([
    'checkout-info',
  ]) as CheckoutInfo
  const { mutateAsync: createPayment } = useCreatePayment()
  const { i18n, t } = useTranslation()

  useEffect(() => {
    if (isYunoLoaded) {
      initSdk()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isYunoLoaded])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    const timer = setTimeout(() => {
      const rootEl = document.getElementById('root-sdk')
      if (isYunoLoaded && rootEl) {
        rootEl.innerHTML = ''
        initSdk()
      }
    }, LANGUAGE_CHANGE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage])

  const showResultScreen = checkoutInfo.payment_link?.show_result_screen ?? false
  const hasReturnUrl = !!checkoutInfo.checkout_return_url

  const getShowPaymentStatus = (): boolean =>
    getVtexResultScreenOverride() ?? (showResultScreen || !hasReturnUrl)

  const initSdk = async () => {
    const yuno = await window.SdkPayments.initialize(
      checkoutInfo.merchant_public_api_key,
      undefined,
      getWhiteLabelOptions()
    )
    const sdkLanguage = i18n.resolvedLanguage

    setYunoInstance(yuno)
    /**
     * checkout configuration
     */
    await yuno.startCheckout({
      showOnlyThesePaymentMethods: getPaymentMethodTypeList(
        checkoutInfo.payment_link.payment_method_types,
      ),
      checkoutSession: checkoutInfo.checkout_session,
      elementSelector: '#root-sdk',
      countryCode: checkoutInfo.payment_link.country,
      language: sdkLanguage as Language,
      showLoading: false,
      showPaymentStatus: getShowPaymentStatus(),
      card: {
        vaultOnSuccess: checkoutInfo?.payment_link.vault_on_success,
        styles: `
          #root {
            .sdk-payments-card-form__title {
              font-size: 18px;
            }

            .sdk-payments-card-icon-bar__content {
              display: none;
            }

            .sdk-payments-card-form__text-security {
              display: none;
            }

            .sdk-payments-card-form__submit {
              margin: 0;

              button {
                padding: 8px 16px;
                border-radius: 8px;
              }

              ${getCustomButtonStyles(checkoutInfo!.payment_link.style)}
            }
          }
        `,
        texts: {
          cardForm: {
            paymentSubmitButton: t('Pay'),
          },
        },
      },
      async createPayment(oneTimeToken: string) {
        if (!getPaymentData.current) {
          throw new Error('Error getting payment function')
        }
        try {
          const payment = await createPayment(getPaymentData.current(oneTimeToken))
          paymentIdRef.current = payment?.code ?? null
          setPublicApiKeyInLS(checkoutInfo!.merchant_public_api_key)
          setCheckoutReturnUrlInLS(checkoutInfo?.checkout_return_url)
          setPaymentIdInLS(payment?.code ?? '')
          setShowResultScreenInLS(showResultScreen)
          const response = await yuno.continuePayment({
            onActionExecuted: (result) => {
              if (!shouldHoldLoader(result)) {
                dispatchLoaderEvent({ showLoader: false })
              }
            }
          })
          if (!shouldHoldLoader(response)) {
            dispatchLoaderEvent({ showLoader: false })
          }
        } catch (_e) {
          dispatchLoaderEvent({ showLoader: false })
        }
      },
      onLoading: ({ isLoading, type }: any) => {
        if (type === 'SEAMLESS_LIST_BUTTONS' && !isLoading) {
          dispatchLoaderEvent({ showLoader: isLoading })
        }
        if (type === 'ONE_TIME_TOKEN' && isLoading) {
          dispatchLoaderEvent({ showLoader: true })
        }
        if (type === 'PAYMENT_RETRY' && !isLoading) {
          // The retried card form is back on screen (e.g. declined after a 3DS
          // setup hold): release the loader so the user can retry.
          dispatchLoaderEvent({ showLoader: false })
        }
      },
      paymentResult: (status: string) => {
        dispatchLoaderEvent({ showLoader: false })
        if (checkoutInfo.checkout_return_url) {
          if (getShowPaymentStatus()) {
            const url = buildCallbackUrl(checkoutInfo.checkout_return_url, status, paymentIdRef.current)
            redirectAfterResultScreen(url)
          } else {
            dispatchLoaderEvent({ showLoader: true })
            redirectTo(checkoutInfo.checkout_return_url)
          }
        }
      },
      error: (err, data) => {
        console.log('Yuno error', { err, data })
        if (shouldSkipContinuePayment(err, data)) {
          return
        }
        yuno.continuePayment()
        dispatchLoaderEvent({ showLoader: false })
      },
      onlyExpressPaymentMethods: (onlyExpress: boolean) => {
        setOnlyExpressPaymentMethods(onlyExpress)
      },
    })

    yuno.mountCheckout()
  }

  getPaymentData.current = (oneTimeToken: string) => {
    return {
      payment_link: checkoutInfo.payment_link.code,
      checkout: {
        session: checkoutInfo.checkout_session,
      },
      payment_method: {
        token: oneTimeToken,
      },
      public_api_key: checkoutInfo.merchant_public_api_key,
      account_code: checkoutInfo!.account_code,
      organization_code: checkoutInfo.organization_code,
      customer_payer: {
        country: checkoutInfo!.country,
        browser_info: {
          user_agent: window.navigator.userAgent,
          color_depth: window.screen.colorDepth.toString(),
          screen_height: window.screen.height.toString(),
          screen_width: window.screen.width.toString(),
          javascript_enabled: true,
          language: i18n.resolvedLanguage || i18n.language,
        },
      },
    }
  }

  const onStartPayment = () => {
    if (yunoInstance) {
      yunoInstance.startPayment()
    }
  }

  return {
    onStartPayment,
    onlyExpressPaymentMethods,
  }
}
